"use client";
import { useEffect, useState } from "react";
import {
  Badge,
  Button,
  Dot,
  Heading,
  Icon,
  Logo,
  Text,
} from "@xenithlabs/xenith-ui";
import { GoogleIcons } from "./GoogleIcons";
import { useSession, useSignIn, useSignUp } from "@clerk/nextjs";
import { OAuthStrategy } from "@clerk/types";
import { Pages } from "@/operator/constants/pages";
import { StagingOnly } from "@repo/utils/components/StagingOnly";
import { DevOnly } from "@repo/utils/components/DevOnly";

import { toast } from "@repo/utils/toast";
export function LoginPage({ flashMessage }: { flashMessage?: string }) {
  const [googleLoginLoading] = useState(false);
  const { signIn } = useSignIn();
  const { signUp, setActive } = useSignUp();
  const { session } = useSession();
  useEffect(() => {
    if (flashMessage) {
      setTimeout(() => {
        toast({
          theme: "destructive",
          title: flashMessage,
        });
      }, 1000);
    }
  }, [flashMessage]);
  if (!signIn || !signUp) return null;

  const signInWith = async (strategy: OAuthStrategy) => {
    if (session?.status === "active") {
      await session.remove();
    }
    await signIn.authenticateWithRedirect({
      strategy,
      redirectUrl: Pages.ssoCallback,
      redirectUrlComplete: Pages.merchants,
    });
  };
  const handleSignIn = async (strategy: OAuthStrategy) => {
    if (!signIn || !signUp) {
      return;
    }
    if (session?.status === "active") {
      window.location.href = `${process.env.NEXT_PUBLIC_URL}`;
      return;
    }
    const userExistsButNeedsToSignIn =
      signUp.verifications.externalAccount.status === "transferable" &&
      signUp.verifications.externalAccount.error?.code ===
        "external_account_exists";

    if (userExistsButNeedsToSignIn) {
      const res = await signIn.create({ transfer: true });

      if (res.status === "complete") {
        setActive({
          session: res.createdSessionId,
        });
      }
    }

    const userNeedsToBeCreated =
      signIn.firstFactorVerification.status === "transferable";

    if (userNeedsToBeCreated) {
      const res = await signUp.create({
        transfer: true,
      });

      if (res.status === "complete") {
        setActive({
          session: res.createdSessionId,
        });
      }
    } else {
      signInWith(strategy);
    }
  };

  return (
    <div className="flex items-center justify-center w-screen h-screen">
      <div className="absolute top-4 left-4 flex gap-2 items-center">
        <Logo />
        <Badge size="sm" theme="primary">
          ADMIN
        </Badge>
      </div>

      <DevOnly fallback={<div />}>
        <div className="absolute left-[226px] top-[18px]">
          <Badge theme="neutral" size="sm">
            <Dot className="bg-bg-warning" />
            <div>DEV</div>
          </Badge>
        </div>
      </DevOnly>
      <StagingOnly fallback={<div />}>
        <div className="absolute left-[226px] top-[18px]">
          <Badge theme="neutral" size="sm">
            <Dot className="bg-bg-warning" />
            <div>STAGING</div>
          </Badge>
        </div>
      </StagingOnly>

      <div className="grid w-[400px]">
        <Heading variant="h2">Welcome back!</Heading>
        <Text variant="subtitle" className="mt-2 mb-6 text-text-weak">
          Login with your Google account.
        </Text>
        <Button
          className="mt-10"
          loading={googleLoginLoading}
          onClick={() => handleSignIn("oauth_google")}
          icon={<Icon icon={<GoogleIcons />} className="text-text-weak " />}
          theme="neutral"
          variant="outline"
        >
          Login With Google
        </Button>
      </div>
    </div>
  );
}
